import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

const CompareContext = createContext();

const STORAGE_KEY = 'wasser_compare';
const MAX_ITEMS = 4;

export const CompareProvider = ({ children }) => {
  const [compareItems, setCompareItems] = useState([]);

  // Load from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        setCompareItems(JSON.parse(stored));
      }
    } catch (e) {
      console.error('Error loading compare:', e);
    }
  }, []);

  // Save to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(compareItems));
    } catch (e) {
      console.error('Error saving compare:', e);
    }
  }, [compareItems]);

  const addToCompare = useCallback((product) => {
    if (!product || !product.id) return false;

    setCompareItems((prev) => {
      // Already at max
      if (prev.length >= MAX_ITEMS) return prev;
      // Already in compare
      if (prev.some((p) => p.id === product.id)) return prev;

      // Store minimal data
      const minimalProduct = {
        id: product.id,
        slug: product.slug,
        name: product.name,
        price: product.price,
        originalPrice: product.originalPrice,
        image: product.image || product.images?.[0] || product.imageUrl,
        category: product.category,
        brand: product.brand,
        stock: product.stock,
        rating: product.rating,
        specs: product.specs || product.specifications || {},
        // Add common furniture specs
        dimensions: product.dimensions,
        material: product.material,
        color: product.color,
        weight: product.weight,
        warranty: product.warranty,
      };

      return [...prev, minimalProduct];
    });
    return true;
  }, []);

  const removeFromCompare = useCallback((productId) => {
    setCompareItems((prev) => prev.filter((p) => p.id !== productId));
  }, []);

  const isInCompare = useCallback(
    (productId) => {
      return compareItems.some((p) => p.id === productId);
    },
    [compareItems]
  );

  const toggleCompare = useCallback(
    (product) => {
      if (isInCompare(product.id)) {
        removeFromCompare(product.id);
        return false;
      } else {
        return addToCompare(product);
      }
    },
    [isInCompare, addToCompare, removeFromCompare]
  );

  const clearCompare = useCallback(() => {
    setCompareItems([]);
  }, []);

  const canAddMore = compareItems.length < MAX_ITEMS;

  // MEMOIZED value to prevent unnecessary re-renders
  const value = useMemo(
    () => ({
      compareItems,
      compareCount: compareItems.length,
      maxItems: MAX_ITEMS,
      addToCompare,
      removeFromCompare,
      isInCompare,
      toggleCompare,
      clearCompare,
      canAddMore,
    }),
    [
      compareItems,
      addToCompare,
      removeFromCompare,
      isInCompare,
      toggleCompare,
      clearCompare,
      canAddMore,
    ]
  );

  return (
    <CompareContext.Provider value={value}>{children}</CompareContext.Provider>
  );
};

export const useCompare = () => {
  const context = useContext(CompareContext);
  if (!context) {
    throw new Error('useCompare must be used within CompareProvider');
  }
  return context;
};

export default CompareContext;
